import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck, ClipboardCheck, Clock, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";

const blank = {
    email: "", name: "", password: "", phone: "",
    requested_role: "employee", requested_designation_id: "", note: "",
};

export default function StaffRegister() {
    const [form, setForm] = useState(blank);
    const [designations, setDesignations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get("/designations").then((r) => setDesignations(r.data || [])).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
        setBusy(true);
        try {
            const body = {
                email: form.email,
                name: form.name,
                password: form.password,
                phone: form.phone,
                requested_role: form.requested_role,
                requested_designation_id: form.requested_role === "employee" ? (form.requested_designation_id || null) : null,
                note: form.note,
            };
            await api.post("/auth/staff-signup", body);
            setSubmitted(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Signup failed");
        } finally { setBusy(false); }
    };

    if (submitted) {
        return (
            <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="staff-pending">
                <Toaster position="top-center" />
                <ClipboardCheck className="w-12 h-12 text-sky-700 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-stone-900 mb-3">Request submitted</h1>
                <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 mb-4 inline-flex items-start gap-2 text-left">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>Your account is awaiting <strong>admin approval</strong>. We'll send you an email when it's approved — typically within one business day. You won't be able to sign in until then.</p>
                </div>
                <div className="text-xs text-stone-500 mt-4">
                    <Link to="/login" className="text-sky-700 underline">Back to sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-12" data-testid="staff-register">
            <Toaster position="top-center" />
            <div className="text-center mb-8">
                <ShieldCheck className="w-10 h-10 text-sky-700 mx-auto mb-3" />
                <div className="label-eyebrow text-sky-700">Staff sign-up</div>
                <h1 className="font-serif text-4xl text-stone-900 mt-2">Join the HPL team</h1>
                <p className="text-sm text-stone-600 mt-2">For HPL employees, editors and admins only. Your account will be activated after admin approval.</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 mb-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <p>Customers, doctors, and distributors should use their dedicated sign-up pages — they're activated instantly, no admin step required.</p>
            </div>

            <form onSubmit={submit} className="bg-white border border-sky-100 p-6 space-y-4">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">I'm signing up as *</Label>
                    <Select value={form.requested_role} onValueChange={(v) => setForm({ ...form, requested_role: v })}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="staff-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="employee">Employee (MR / Packing / Stock / Accounts)</SelectItem>
                            <SelectItem value="editor">Editor (manage admin panel content)</SelectItem>
                            <SelectItem value="admin">Admin (full system access)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {form.requested_role === "employee" && designations.length > 0 && (
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Requested role / designation</Label>
                        <Select value={form.requested_designation_id} onValueChange={(v) => setForm({ ...form, requested_designation_id: v })}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="staff-designation"><SelectValue placeholder="(admin will assign if left blank)" /></SelectTrigger>
                            <SelectContent>
                                {designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <p className="text-[10px] text-stone-500 mt-1">Admin can change this when approving your account.</p>
                    </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Full name *</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none border-stone-300 mt-1" data-testid="staff-name" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Phone</Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="staff-phone" />
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-none border-stone-300 mt-1" data-testid="staff-email" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Password * (min 6 chars)</Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="rounded-none border-stone-300 mt-1" data-testid="staff-password" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Note for admin (optional)</Label>
                    <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="e.g. recommended by Mr. Sharma · Andheri territory" className="rounded-none border-stone-300 mt-1" data-testid="staff-note" />
                </div>

                <Button type="submit" disabled={busy} className="rounded-none w-full bg-sky-700 hover:bg-sky-800 text-stone-50 py-6" data-testid="staff-submit">
                    {busy ? "Submitting…" : "Submit for approval"}
                </Button>
            </form>

            <div className="text-center text-xs text-stone-500 mt-4">
                Already approved? <Link to="/login" className="text-sky-700 underline">Sign in</Link>
            </div>
        </div>
    );
}
